import { useState } from "react";
import { captureException } from "@sentry/nextjs";
import { sendFormEvent, sendLeadGenerationEvent } from "@/app/ga4/eventHandler";
import { createContactLead } from "@/app/lib/actions/leads";

type AlertStatus = "success" | "error";

function getInitialFormState() {
  return {
    fname: "",
    phone: "",
    email: "",
    comment: "",
    company: "",
  };
}

export function useContactForm() {
  const [formData, setFormData] = useState(getInitialFormState());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationStatus, setConfirmationStatus] =
    useState<AlertStatus | null>(null);

  async function saveLead(campaignId?: string, turnstileToken?: string) {
    setIsSubmitting(true);

    if (process.env.NODE_ENV !== "development") {
      sendFormEvent("CONTACT_FORM", "SUBMIT");
    }

    const payload = {
      ...formData,
      // Add campaignId if it exists
      comment: campaignId
        ? `Campaign ID: ${campaignId}${
            formData.comment ? ` - ${formData.comment}` : ""
          }`
        : formData.comment,
      turnstileToken: turnstileToken ?? "",
    };

    let didSubmitSucceeded = false;

    try {
      const response = await createContactLead(payload);

      if (!response.success) {
        throw new Error();
      }

      if (process.env.NODE_ENV !== "development") {
        sendLeadGenerationEvent("CONTACT_FORM");
      }

      didSubmitSucceeded = true;
    } catch (error: unknown) {
      if (process.env.NODE_ENV !== "development") {
        sendFormEvent("CONTACT_FORM", "INVALID/ERROR");
      }

      captureException(error);
    }

    setConfirmationStatus(didSubmitSucceeded ? "success" : "error");
    setConfirmationDialogOpen(true);
    setIsSubmitting(false);

    return didSubmitSucceeded;
  }

  function handleConfirmationDialogClose() {
    setFormData(getInitialFormState());
    setConfirmationStatus(null);
    setConfirmationDialogOpen(false);
  }

  function handleCorrectData() {
    setConfirmationStatus(null);
    setConfirmationDialogOpen(false);
  }

  return {
    formData,
    setFormData,
    confirmationDialogOpen,
    confirmationStatus,
    isSubmitting,
    handleCorrectData,
    handleConfirmationDialogClose,
    saveLead,
  };
}
